import { FC, ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { useUserStore } from '../stores/userStore';

// Define the shape of our context
interface AuthContextType {
  isLogged: boolean | null;
  setIsLogged: (value: boolean | null) => void;
}

interface AuthProviderProps {
  children: ReactNode;
}

// Create the context with default values
const AuthContext = createContext<AuthContextType>({
  isLogged: null,
  setIsLogged: () => {},
});

/**
 * AuthProvider component that manages authentication state
 * Uses Zustand user store to check authentication and avoid duplicate API calls
 */
export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [isLogged, setIsLogged] = useState<boolean | null>(null);
  const fetchUser = useUserStore((state) => state.fetchUser);
  const user = useUserStore((state) => state.user);
  const loading = useUserStore((state) => state.loading);

  // Initialize user fetch on mount (this populates the Zustand store)
  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Update auth status based on user store state
  useEffect(() => {
    if (!loading) {
      setIsLogged(user !== null);
    }
  }, [user, loading]);

  return (
    <AuthContext.Provider value={{ isLogged, setIsLogged }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;
